<template>
  <v-card
    class="truecar-vehicle-card"
    elevation="1"
    @click="$emit('vehicle-clicked', vehicle.id)"
  >
    <!-- Vehicle Image -->
    <div class="image-container">
      <v-carousel
        v-if="vehicle.images && vehicle.images.length > 0"
        height="200"
        hide-delimiters
        show-arrows-on-hover
        class="vehicle-carousel"
      >
        <v-carousel-item
          v-for="(image, i) in vehicle.images"
          :key="i"
        >
          <v-img
            :src="getImageSrc(image.image)"
            height="200"
            cover
            @error="handleImageError"
            class="vehicle-image"
          >
            <template v-slot:placeholder>
              <v-img
                :src="fallbackImage"
                height="200"
                cover
                class="vehicle-image"
              ></v-img>
            </template>
          </v-img>
        </v-carousel-item>
      </v-carousel>

      <!-- Fallback single image -->
      <v-img
        v-else
        :src="fallbackImage"
        height="200"
        cover
        class="vehicle-image"
      ></v-img>

      <!-- Save Button -->
      <v-btn
        icon
        small
        color="white"
        class="save-btn"
        @click.stop="toggleSave"
      >
        <v-icon class="material-icons-round" :color="isSaved ? 'red' : 'white'">
          {{ isSaved ? 'favorite' : 'favorite_border' }}
        </v-icon>
      </v-btn>
    </div>

    <!-- Vehicle Info -->
    <v-card-text class="vehicle-info">
      <!-- Price -->
      <div class="price-section">
        <div class="price-container">
          <span class="price">{{ formatPrice(vehicle.amount) }}</span>
          <span class="price-label">Price</span>
        </div>
        <div v-if="vehicle.estimated_payment" class="payment-container mt-5" @click.stop="openPaymentCalculator">
          <span class="estimated-payment clickable">${{ customPayment || vehicle.estimated_payment }}/mo</span>
          <span class="payment-label">Est. Payment</span>
        </div>
      </div>

      <!-- Vehicle Title -->
      <h3 class="vehicle-title">{{ getVehicleTitle() }}</h3>

      <!-- Vehicle Details -->
      <div class="vehicle-details">
        <div class="detail-item" v-if="vehicle.odometer">
          <span class="detail-value">{{ formatMileage(vehicle.odometer) }}</span>
        </div>
        <div class="detail-item" v-if="vehicle.exterior_color">
          <span class="detail-value">{{ vehicle.exterior_color }}</span>
        </div>
        <div class="detail-item" v-if="vehicle.transmission">
          <span class="detail-value">{{ vehicle.transmission }}</span>
        </div>
      </div>

      <!-- Dealer Info -->
      <div class="dealer-info" v-if="vehicle.dealer_name">
        <span class="dealer-name">{{ vehicle.dealer_name }}</span>
        <span class="dealer-location" v-if="vehicle.dealer_location">
          • {{ vehicle.dealer_location }}
        </span>
      </div>
    </v-card-text>

    <!-- Action Buttons -->
    <v-card-actions class="card-actions">
      <v-btn
        text
        small
        class="action-btn"
        @click.stop="contactDealer"
      >
        <v-icon left small class="material-icons-round">phone</v-icon>
        Contact Dealer
      </v-btn>
      
      <v-spacer></v-spacer>
      
      <v-btn
        text
        small
        class="action-btn"
        @click.stop="viewDetails"
      >
        View Details
        <v-icon right small class="material-icons-round">arrow_forward</v-icon>
      </v-btn>
    </v-card-actions>

    <!-- Payment Calculator Modal -->
    <v-dialog v-model="showPaymentCalculator" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          Payment Calculator
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <h4 class="mb-2">{{ getVehicleTitle() }}</h4>
            <div class="text-h6 font-weight-bold text--primary">
              {{ formatPrice(vehicle.amount) }}
            </div>
            <div class="text-caption text--secondary mt-2">
              Default values are set by admin. Customize to see how different terms affect your payment.
            </div>
          </div>

          <v-form ref="calculatorForm">
            <!-- APR Rate -->
            <v-text-field
              v-model="calculator.aprRate"
              label="APR Rate (%)"
              type="number"
              step="0.01"
              min="0"
              max="30"
              outlined
              dense
              suffix="%"
              @input="calculatePayment"
              :rules="[v => !!v || 'APR is required', v => v >= 0 || 'APR must be positive', v => v <= 30 || 'APR must be 30% or less']"
            ></v-text-field>

            <!-- Down Payment -->
            <v-text-field
              v-model="calculator.downPayment"
              label="Down Payment"
              type="number"
              min="0"
              outlined
              dense
              prefix="$"
              @input="calculatePayment"
              :rules="[v => v >= 0 || 'Down payment must be positive', v => v <= vehiclePrice || 'Down payment cannot exceed vehicle price']"
            ></v-text-field>

            <!-- Loan Term (User-editable dropdown) -->
            <v-select
              v-model="calculator.termMonths"
              :items="loanTermOptions"
              label="Loan Term"
              outlined
              dense
              @change="calculatePayment"
              :rules="[v => !!v || 'Loan term is required']"
            ></v-select>

            <!-- Calculated Payment Display -->
            <v-card class="mt-4 pa-4" color="green lighten-5" outlined>
              <div class="text-center">
                <div class="text-h4 font-weight-bold green--text">
                  ${{ calculatedPayment }}/mo
                </div>
                <div class="text-subtitle-2 green--text">
                  Estimated Monthly Payment
                </div>
              </div>
            </v-card>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn text @click="resetCalculator">
            Reset
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="closePaymentCalculator">
            Cancel
          </v-btn>
          <v-btn color="green" dark @click="applyCalculatedPayment">
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'TrueCarVehicleCard',
  props: {
    vehicle: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      fallbackImage: require('@/assets/img/coming-soon.jpg'),
      brokenImages: new Set(),
      isSaved: false,
      showPaymentCalculator: false,
      customPayment: null,
      calculator: {
        aprRate: null,
        downPayment: 0,
        termMonths: null
      },
      calculatedPayment: 0,
      loanTermOptions: [
        { text: '36 months', value: 36 },
        { text: '48 months', value: 48 },
        { text: '60 months', value: 60 },
        { text: '72 months', value: 72 },
        { text: '84 months', value: 84 },
        { text: '96 months', value: 96 }
      ]
    }
  },
  computed: {
    vehiclePrice() {
      return parseFloat(this.vehicle?.amount || this.vehicle?.price || 0)
    }
  },
  mounted() {
    // Initialize calculator defaults when component mounts
    this.initializeCalculatorDefaults()
  },
  methods: {
    getImageSrc(imageSrc) {
      if (!imageSrc || this.brokenImages.has(imageSrc)) {
        return this.fallbackImage
      }
      
      if (imageSrc.startsWith('http') || imageSrc.startsWith('data:image')) {
        return imageSrc
      }
      
      return this.fallbackImage
    },

    handleImageError(event) {
      const originalSrc = event.target.src
      if (originalSrc && !originalSrc.includes('coming-soon.jpg')) {
        this.brokenImages.add(originalSrc)
      }
      event.target.src = this.fallbackImage
    },

    getVehicleTitle() {
      if (!this.vehicle) return 'Vehicle'
      
      const parts = []
      if (this.vehicle.year) parts.push(this.vehicle.year)
      if (this.vehicle.make) parts.push(this.vehicle.make)
      if (this.vehicle.model) parts.push(this.vehicle.model)
      if (this.vehicle.trim) parts.push(this.vehicle.trim)
      
      return parts.join(' ') || 'Vehicle'
    },

    formatPrice(price) {
      if (!price) return 'Call for pricing'

      const numPrice = parseFloat(price)
      if (isNaN(numPrice) || numPrice <= 0) return 'Call for pricing'

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numPrice)
    },

    formatMileage(mileage) {
      if (!mileage) return 'N/A'
      
      const numMileage = parseInt(mileage)
      if (isNaN(numMileage)) return 'N/A'
      
      return new Intl.NumberFormat('en-US').format(numMileage) + ' mi'
    },

    toggleSave() {
      this.isSaved = !this.isSaved
      // TODO: Implement save functionality
      console.log('Toggle save vehicle:', this.vehicle.id)
    },

    contactDealer() {
      // Open Contact Dealer AI Assistant modal
      this.$emit('contact-dealer', this.vehicle)
    },

    viewDetails() {
      this.$emit('vehicle-clicked', this.vehicle.id)
    },

    // Initialize calculator defaults
    initializeCalculatorDefaults() {
      // Set default values without opening the calculator
      this.calculator.aprRate = this.vehicle.loan_apr_rate || 6.70
      this.calculator.downPayment = 0

      // Ensure loan term matches one of the dropdown options
      const defaultTerm = this.vehicle.loan_term_months || 84
      const validTerm = this.loanTermOptions.find(option => option.value === defaultTerm)
      this.calculator.termMonths = validTerm ? defaultTerm : 84

      console.log('🔧 Payment Calculator - Initialized defaults:', {
        aprRate: this.calculator.aprRate,
        termMonths: this.calculator.termMonths,
        adminTerm: this.vehicle.loan_term_months,
        validTerm: !!validTerm
      })
    },

    // Payment Calculator Methods
    openPaymentCalculator() {
      // Ensure defaults are set (in case vehicle data changed)
      this.initializeCalculatorDefaults()

      // Calculate initial payment with current values
      this.calculatePayment()

      console.log('🔧 Payment Calculator - Opening with values:', {
        aprRate: this.calculator.aprRate,
        termMonths: this.calculator.termMonths,
        downPayment: this.calculator.downPayment,
        vehiclePrice: this.vehiclePrice
      })

      this.showPaymentCalculator = true
    },

    closePaymentCalculator() {
      this.showPaymentCalculator = false
    },

    calculatePayment() {
      try {
        const principal = this.vehiclePrice - (parseFloat(this.calculator.downPayment) || 0)
        const monthlyRate = (parseFloat(this.calculator.aprRate) || 0) / 100 / 12
        const numPayments = parseInt(this.calculator.termMonths) || 84

        if (principal <= 0) {
          this.calculatedPayment = '0.00'
          return
        }

        if (monthlyRate === 0) {
          // No interest case
          this.calculatedPayment = (principal / numPayments).toFixed(2)
          return
        }

        // Standard loan payment formula: P * [r(1+r)^n] / [(1+r)^n - 1]
        const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1)

        this.calculatedPayment = monthlyPayment.toFixed(2)
      } catch (error) {
        console.error('Error calculating payment:', error)
        this.calculatedPayment = '0.00'
      }
    },

    applyCalculatedPayment() {
      // Update the custom payment to show on the card
      this.customPayment = this.calculatedPayment
      this.closePaymentCalculator()
    },

    resetCalculator() {
      // Reset to original admin defaults using the initialization method
      this.initializeCalculatorDefaults()

      console.log('🔄 Payment Calculator - Reset to admin defaults')

      this.calculatePayment()
      this.customPayment = null
    }
  }
}
</script>

<style scoped>
/* TrueCar Vehicle Card */
.truecar-vehicle-card {
  background: white;
  border-radius: 8px !important;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.truecar-vehicle-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-color: #d1d5db;
}

/* Image Container */
.image-container {
  position: relative;
  overflow: hidden;
}

.vehicle-carousel {
  border-radius: 0;
}

.vehicle-image {
  transition: transform 0.3s ease;
}

.truecar-vehicle-card:hover .vehicle-image {
  transform: scale(1.02);
}

.save-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5) !important;
  z-index: 2;
}

.save-btn:hover {
  background: rgba(0, 0, 0, 0.7) !important;
}

/* Vehicle Info */
.vehicle-info {
  padding: 20px !important;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', Arial, sans-serif;
}

.price-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.price-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.payment-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.price {
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.price-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 2px;
}

.estimated-payment {
  font-size: 1.375rem;
  font-weight: 700;
  color: #10b981;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

.estimated-payment.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.estimated-payment.clickable:hover {
  color: #059669;
  text-decoration: underline;
}

.payment-label {
  font-size: 0.75rem;
  color: #10b981;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 2px;
}

.vehicle-title {
  font-size: 1.125rem !important;
  font-weight: 700 !important;
  color: #111827 !important;
  margin: 0 !important;
  line-height: 1.3;
  letter-spacing: -0.01em;
  font-family: inherit;
}

.vehicle-details {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
}

.detail-item {
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.4;
}

.detail-value {
  font-weight: 600;
  color: #374151;
}

.dealer-info {
  margin-top: auto;
  padding-top: 12px;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.4;
}

.dealer-name {
  font-weight: 600;
  color: #4b5563;
}

.dealer-location {
  font-weight: 400;
  color: #6b7280;
}

/* Card Actions */
.card-actions {
  padding: 16px 20px !important;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  color: #1976d2 !important;
  padding: 8px 12px !important;
  min-width: auto !important;
  height: 36px !important;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: 0.01em;
}

.action-btn:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
  color: #1565c0 !important;
}

.action-btn::before {
  background-color: transparent !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .vehicle-info {
    padding: 16px !important;
    gap: 10px;
  }

  .price {
    font-size: 1.375rem;
  }

  .vehicle-title {
    font-size: 1rem !important;
  }

  .vehicle-details {
    gap: 12px;
  }

  .detail-item {
    font-size: 0.85rem;
  }

  .card-actions {
    padding: 12px 16px !important;
  }

  .action-btn {
    font-size: 0.8rem !important;
    padding: 6px 10px !important;
    height: 32px !important;
  }
}

@media (max-width: 600px) {
  .vehicle-info {
    padding: 14px !important;
    gap: 8px;
  }

  .vehicle-details {
    flex-direction: column;
    gap: 6px;
  }

  .price {
    font-size: 1.25rem;
  }

  .vehicle-title {
    font-size: 0.95rem !important;
    line-height: 1.4;
  }

  .detail-item {
    font-size: 0.8rem;
  }

  .dealer-info {
    font-size: 0.75rem;
    padding-top: 8px;
  }
}
</style>
